import React, { useState, useCallback, useEffect } from 'react';
import { TSession } from './Sessions';
import { IconButton, Menu, MenuItem, Divider, ListItemIcon, ListItemText } from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { Privilege } from '../../api';
import useHasPrivilege from '../Util/useHasPrivilege';
import SessionDuplicateDialog from './SessionDuplicateDialog';
import { usePopupState, bindTrigger, bindMenu } from 'material-ui-popup-state/hooks';
import { RouteComponentProps, withRouter } from 'react-router';
import { Delete, CloudDownload, ExitToApp, FileCopyOutlined } from '@material-ui/icons';
import DeleteSession from '../Util/DeleteSession';
import sessionsService from '../../services/sessions';
import { CheckCircle } from '@material-ui/icons';

interface Props extends RouteComponentProps {
  session: TSession;
}
type Artifact = {
  filename: string,
  url: string,
}

const SessionDownload: React.FunctionComponent<Props> = (props) => {
  const popupState = usePopupState({ variant: 'popover', popupId: 'fileMenu' });
  const [session, setSession] = useState<TSession>();
  const [deleting, setDeleting] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const isAdmin = useHasPrivilege(Privilege.Admin);
  const [currentSession, setCurrentSession] = useState<TSession>();
  const [artifacts, setArtifacts] = useState<Artifact[]>([]);

  let downloads;
  if (artifacts.length > 0) {
    downloads = artifacts.map((artifact) => (
      <div key={artifact.filename} onClick={() => (window.location.href = artifact.url)}>
        <MenuItem>
          <ListItemIcon>
            <CloudDownload fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={artifact.filename} />
        </MenuItem>
      </div>
    ));
  }

  const onClickDelete = () => {
    setSession(props.session);
    setDeleting(true);
    popupState.close();
  };

  const onClickExport = useCallback(async () => {
    await sessionsService.exportSessionFile(props.session.id);
  }, [props.session.id]);

  const onClickApprove = useCallback(async () => {
    await sessionsService.approveSession(props.session.id);
    popupState.close();

    setCurrentSession(currentSession && { ...currentSession, approved: true });

  }, [currentSession, setCurrentSession, props.session.id, popupState]);

  useEffect(() => {
    
    if (!popupState.isOpen) {
      return;
    }
   
    (async () => {
      const [result, artifactResult] = await Promise.all([
        sessionsService.getSession(props.session.id),
        sessionsService.listArtifacts(props.session.id),
      ]);
      setCurrentSession(result!);
      setArtifacts(artifactResult?.items ?? []);
    })();
  }, [popupState.isOpen, props.session.id, setCurrentSession])

  return (
    <div>
      <IconButton {...bindTrigger(popupState)}>
        <MoreVertIcon />
      </IconButton>
      <Menu {...bindMenu(popupState)}>
        {
          props.session.lastRun === null && <MenuItem onClick={onClickDelete}>
            <ListItemIcon>
              <Delete fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Delete session" />
          </MenuItem>
        }
        <MenuItem onClick={onClickExport}>
          <ListItemIcon>
            <ExitToApp fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Export session" />
        </MenuItem>
        {isAdmin && (
          <MenuItem
            onClick={() => {
              popupState.close();
              setDuplicating(true);
            }}
          >
            <ListItemIcon>
              <FileCopyOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Duplicate session" />
          </MenuItem>
        )}
        {downloads && currentSession && !currentSession.approved && <MenuItem onClick={onClickApprove}>
          <ListItemIcon>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/></svg>
          </ListItemIcon>
          <ListItemText primary="Approve session" />
        </MenuItem>}
        {downloads && currentSession && currentSession.approved && <MenuItem>
          <ListItemIcon>
            <CheckCircle color="secondary" />
          </ListItemIcon>
          <ListItemText primary="Session is approved" />
        </MenuItem>}
        {downloads && <Divider component="li" />}
        {downloads}
      </Menu>
      <DeleteSession
        open={deleting}
        session={session}
        key={session ? session.id : '-'}
        onClose={() => setDeleting(false)}
      />
      {isAdmin && (
        <SessionDuplicateDialog
          open={duplicating}
          sessionIds={[String(props.session.id)]}
          onClose={() => setDuplicating(false)}
        />
      )}
    </div>
  );
};

export default withRouter(SessionDownload);
